import React, { useCallback } from 'react'

import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useRouteMatch } from 'react-router'

import { SideBarBlock, SideBarBlockItem } from '@components/UI/SideBar'
import ConnectedTrackedRoute from '@components/UI/TrackedRoute/ConnectedTrackedRoute'
import { IMobileMenuControlsContextProps } from '@context/MobileMenuControls'
import { IModalContextProps } from '@context/Modal/Provider'
import { EventTrackingNames, PageRoutes } from '@enums'
import { withMobileMenuControls, withModal } from '@hocs'
import { IApplicationState } from '@store'
import {
  selectAudienceReportingLink,
  selectIsCampaignFetched,
  selectIsSocialCampaign,
  selectPostCampaignReportLink,
} from '@store/campaigns'
import {
  selectCampaignId,
  selectCurrentSubmissionFilter,
  selectPathname,
  selectSearchStatus,
} from '@store/router'
import { fetchSubmissions, selectIsFetching, selectSubmissionsStats } from '@store/submissions'
import { isEmpty, objectToQueryString } from '@utils'

export interface IInternalProps extends IMobileMenuControlsContextProps, IModalContextProps {
  hasSubmissionStats?: boolean
  campaignId?: number
  pathname?: string
  searchStatus?: string
  hasPostCampaignReportLink?: boolean
  isCampaignFetched?: boolean
  isFetchingSubmissions?: boolean
  fetchSubmissions?: typeof fetchSubmissions
  filter?: string
  isSocial?: boolean
  hasAudienceReportingLink?: boolean
}

const ReportsList: React.FC<IInternalProps> = ({
  hasSubmissionStats,
  campaignId,
  pathname,
  searchStatus,
  hasPostCampaignReportLink,
  hideMobileMenu,
  isCampaignFetched,
  isFetchingSubmissions,
  fetchSubmissions: fetchSubmissionsAction,
  filter,
  isSocial,
  hasAudienceReportingLink,
}) => {
  React.useEffect(() => {
    if (!hasSubmissionStats && isCampaignFetched && !isFetchingSubmissions && isSocial) {
      fetchSubmissionsAction!(campaignId!, { filter })
    }
  }, [isCampaignFetched])

  if (!isSocial || !hasPostCampaignReportLink) {
    return null
  }

  const queryString = objectToQueryString({
    status: searchStatus,
  })

  return (
    <React.Fragment>
      <SideBarBlock
        header={<FormattedMessage id="inbox.sidebar.reports" />}
        items={[
          hasPostCampaignReportLink && (
            <ConnectedTrackedRoute
              key="reports-sidebar-pcr"
              eventName={EventTrackingNames.ViewCampaignMetrics}
              campaignId={campaignId!}
              additionalProperties={{
                current_filter: undefined,
              }}
            >
              <SideBarBlockItem
                text={<FormattedMessage id="inbox.sidebar.reports.metrics" />}
                active={pathname!.includes(`${PageRoutes.Reports}/pcr`)}
                to={{
                  pathname: `/${PageRoutes.Campaigns}/${campaignId}/${PageRoutes.Reports}/pcr`,
                  search: queryString,
                }}
                onClick={hideMobileMenu}
                elementName="reports-sidebar-metrics"
              />
            </ConnectedTrackedRoute>
          ),

          hasAudienceReportingLink && (
            <ConnectedTrackedRoute
              eventName={EventTrackingNames.ViewAudienceReporting}
              campaignId={campaignId!}
              key="reports-audience-reporting-tracking"
            >
              <SideBarBlockItem
                key="reports-audience-reporting"
                text={<FormattedMessage id="core.text.audience" />}
                active={pathname!.includes(`${PageRoutes.Reports}/audience`)}
                to={{
                  pathname: `/${PageRoutes.Campaigns}/${campaignId}/${PageRoutes.Reports}/audience`,
                  search: queryString,
                }}
              />
            </ConnectedTrackedRoute>
          ),
        ]}
      />
    </React.Fragment>
  )
}

const ReportsListConnectedInner: React.FC<IMobileMenuControlsContextProps & IModalContextProps> = (
  contextProps,
) => {
  const match = useRouteMatch()
  const location = useLocation()
  const dispatch = useDispatch()
  const campaignId = selectCampaignId(match)!
  const isSocial = useSelector((state: IApplicationState) =>
    selectIsSocialCampaign(state, campaignId),
  )
  const hasAudienceReportingLink = useSelector((state: IApplicationState) =>
    !isEmpty(selectAudienceReportingLink(state, campaignId)),
  )
  const filter = useSelector((state: IApplicationState) => selectCurrentSubmissionFilter(state))
  const isCampaignFetched = useSelector((state: IApplicationState) =>
    selectIsCampaignFetched(state, campaignId),
  )
  const isFetchingSubmissions = useSelector((state: IApplicationState) => selectIsFetching(state))
  const pathname = useSelector((state: IApplicationState) => selectPathname(state))
  const searchStatus = selectSearchStatus(location)
  const hasSubmissionStats = useSelector(
    (state: IApplicationState) => !isEmpty(selectSubmissionsStats(state, campaignId)),
  )
  const hasPostCampaignReportLink = useSelector((state: IApplicationState) =>
    Boolean(selectPostCampaignReportLink(state, campaignId)),
  )
  const fetchSubmissionsCallback = useCallback(
    (cid, meta, isFetchNext?) => dispatch(fetchSubmissions(cid, meta, isFetchNext)),
    [dispatch],
  )

  return (
    <ReportsList
      {...contextProps}
      campaignId={campaignId}
      isSocial={isSocial}
      filter={filter}
      isCampaignFetched={isCampaignFetched}
      isFetchingSubmissions={isFetchingSubmissions}
      pathname={pathname}
      searchStatus={searchStatus}
      hasSubmissionStats={hasSubmissionStats}
      hasPostCampaignReportLink={hasPostCampaignReportLink}
      hasAudienceReportingLink={hasAudienceReportingLink}
      fetchSubmissions={fetchSubmissionsCallback}
    />
  )
}

const ReportsListConnected = withMobileMenuControls(withModal(ReportsListConnectedInner))

export { ReportsList }

export default ReportsListConnected
